import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Plus, RotateCw } from "lucide-react";
import { showErrorAlert } from "../../../../../components/HelperAlerts/HelpersAlerts.jsx";

const EMPTY_FORM = {
    provider: "", range_name: "", sku: "", display_name: "",
    color_hex: "#808080", finish: "estandar", is_active: true, notes: "",
};

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

export default function FilamentosSection() {
    const [items, setItems] = useState([]);
    const [state, setState] = useState("loading");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setState("loading");
        axios.get("/fulfillment-admin/filaments/")
            .then(({ data }) => { setItems(Array.isArray(data) ? data : []); setState("ready"); })
            .catch((err) => {
                console.error("Error loading filament references:", err);
                setState(err.response?.status === 403 ? "forbidden" : "error");
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await axios.post("/fulfillment-admin/filaments/", form);
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            console.error("Error creating filament reference:", err);
            showErrorAlert({ title: "No se pudo crear la referencia", text: formatApiError(err, "Revisa los datos.") });
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (reference) => {
        try {
            await axios.patch(`/fulfillment-admin/filaments/${reference.id}/`, { is_active: !reference.is_active });
            load();
        } catch (err) {
            console.error("Error toggling filament reference:", err);
            showErrorAlert({ title: "No se pudo actualizar", text: formatApiError(err, "Inténtalo de nuevo.") });
        }
    };

    if (state === "loading") {
        return <div className="flex justify-center py-10"><span className="loading loading-spinner loading-lg text-primary" /></div>;
    }
    if (state === "forbidden") {
        return (
            <div role="alert" className="alert alert-warning text-sm">
                <AlertTriangle className="w-4 h-4" />
                Tu cuenta no tiene el permiso <code>fulfillment.manage_filament_references</code>.
            </div>
        );
    }
    if (state === "error") {
        return (
            <div role="alert" className="alert alert-error text-sm">
                <AlertTriangle className="w-4 h-4" />
                No se pudieron cargar las referencias.
                <button type="button" className="btn btn-xs gap-1" onClick={load}><RotateCw className="w-3 h-3" />Reintentar</button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-base-content/60">
                    Referencias PLA administrables. Solo las activas se ofrecen en el editor/solicitud.
                </p>
                <button type="button" className="btn btn-sm btn-primary gap-1" onClick={() => setShowForm((v) => !v)}>
                    <Plus className="w-4 h-4" /> Nueva referencia
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleCreate} className="glassCard rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Proveedor</span>
                        <input required className="input input-bordered input-sm" value={form.provider}
                            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Gama</span>
                        <input className="input input-bordered input-sm" value={form.range_name}
                            onChange={(e) => setForm((f) => ({ ...f, range_name: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">SKU</span>
                        <input required className="input input-bordered input-sm" value={form.sku}
                            onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Nombre visible</span>
                        <input required className="input input-bordered input-sm" value={form.display_name}
                            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Color</span>
                        <input type="color" className="input input-bordered input-sm h-10" value={form.color_hex}
                            onChange={(e) => setForm((f) => ({ ...f, color_hex: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Acabado</span>
                        <input required className="input input-bordered input-sm" value={form.finish}
                            onChange={(e) => setForm((f) => ({ ...f, finish: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1 sm:col-span-2">
                        <span className="label-text text-xs">Notas</span>
                        <textarea className="textarea textarea-bordered textarea-sm" value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                    </label>
                    <label className="flex items-center gap-2 sm:col-span-2">
                        <input type="checkbox" className="checkbox checkbox-sm" checked={form.is_active}
                            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                        <span className="text-sm">Activa desde el momento de creación</span>
                    </label>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                            {saving ? <span className="loading loading-spinner loading-xs" /> : "Crear"}
                        </button>
                    </div>
                </form>
            )}

            {items.length === 0 ? (
                <p className="text-sm text-base-content/50">No hay referencias todavía.</p>
            ) : (
                <div className="overflow-x-auto glassCard rounded-xl">
                    <table className="table table-zebra">
                        <thead>
                            <tr>
                                <th>Color</th>
                                <th>Nombre</th>
                                <th>Proveedor</th>
                                <th>SKU</th>
                                <th>Acabado</th>
                                <th>Material</th>
                                <th>Activa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((ref) => (
                                <tr key={ref.id}>
                                    <td><span className="inline-block w-5 h-5 rounded-full border border-base-300" style={{ backgroundColor: ref.color_hex }} /></td>
                                    <td className="font-medium">{ref.display_name}</td>
                                    <td>{ref.provider} {ref.range_name}</td>
                                    <td className="font-mono text-xs">{ref.sku}</td>
                                    <td>{ref.finish}</td>
                                    <td className="uppercase text-xs">{ref.material}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${ref.is_active ? "btn-success" : "btn-ghost"}`}
                                            onClick={() => toggleActive(ref)}
                                        >
                                            {ref.is_active ? "Activa" : "Inactiva"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
